import React, { ComponentPropsWithoutRef, MouseEvent } from 'react';
import SeoResource from '../../../../../../services/resources/SeoResource';
import classnames from 'classnames';
import HeartIcon from '../../../../../../assets/logo/HeartIcon';
import PriceComparison from '../../../../../../components-library/menu/PriceComparison';
import PlusIcon from '../../../../../../assets/logo/PlusIcon';
import useProductLikes from '../../../../../../hooks/global/useProductLikes';
import { WithActualPrice } from '../../../../../../providers/MenuProvider';
import { ButtonTypes, ProductInterface } from '../../../../../../services/exports/Interfaces';
import useTheme from '../../../../../../hooks/ui/useTheme';

type RecommendedProductItemProps = ComponentPropsWithoutRef<'article'> & {
  product: WithActualPrice<ProductInterface>;
};

const RecommendedProductItem = ({ product, className, ...rest }: RecommendedProductItemProps) => {
  const { toggleProductLike, isLiked: checkIsLiked } = useProductLikes();
  const theme = useTheme();
  const isLiked = checkIsLiked(product.id);
  const likesCount = product.likes_count;
  const onLikeClick = (e: MouseEvent<HTMLDivElement>) => {
    e.stopPropagation();
    toggleProductLike(product.id);
  };

  return (
    <article
      className={classnames(
        'border rounded-lg h-full w-[306px] flex-shrink-0 snap-center flex justify-between overflow-hidden bg-brand-card',
        className,
      )}
      {...rest}
    >
      <div className="space-y-sm flex-shrink-0 p-md">
        <h3 className="text-base font-normal">{product.name}</h3>
        <div className="flex space-x-6 items-center">
          <PriceComparison
            actualPrice={product.actual_price}
            originalPrice={product.base_price}
            className="text-brand-text-secondary"
          />
          <div className="flex items-center justify-center space-x-2" onClick={onLikeClick}>
            <HeartIcon
              filled={!!isLiked}
              className={classnames('h-3', {
                'fill-danger': isLiked,
                'fill-mobile-secondary': !isLiked,
              })}
            />
            <span className="text-xs text-brand-text-secondary">{likesCount}</span>
          </div>
        </div>
      </div>
      <figure className="overflow-hidden rounded-r-lg relative w-[90px] h-full">
        {product?.thumbnail_url && (
          <img
            src={product.thumbnail_url}
            alt={SeoResource.toAltText(`${product?.name} image`)}
            className="object-cover w-full h-full"
          />
        )}
        <span className="bottom-sm size-6 right-sm absolute bg-brand-primary rounded-[5px] flex items-center justify-center">
          <PlusIcon className="size-3" color={theme.buttons[ButtonTypes.primary].text} />
        </span>
      </figure>
    </article>
  );
};

export default RecommendedProductItem;
